/**
 * MONSTER TOWN — SAVES ARE SACRED. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * Losing a kid's town is unacceptable and is made impossible here, not by care
 * elsewhere. The guarantees this file provides:
 *
 *  1. TWO SLOTS, one per kid, fully independent ('a' and 'b').
 *  2. VERSIONED ENVELOPE + per-section version, with a migration chain.
 *  3. DEFENSIVE HYDRATION — a section that throws or returns garbage yields its
 *     defaults and a warning; it can never take the load down.
 *  4. ROLLING BACKUP — the previous good save is copied to a backup key before
 *     the new one lands. A corrupt main always falls back to the backup.
 *  5. SINGLE-WRITER COMMIT — one writer, debounced; overlapping requests
 *     coalesce. Verified read-back before the old save is retired.
 *  6. NEVER MID-TRANSITION — commits are deferred while a scene transition is
 *     in flight (bus events do this automatically) and flushed after.
 *  7. AUTOSAVE on every meaningful change plus pagehide/visibilitychange, which
 *     is how an iPad actually gets closed.
 *
 * HOW A CREW PERSISTS ITS STATE (the only thing you need to do):
 *
 *   registerSaveSection<MyState>({
 *     id: 'venue',            // unique, stable forever
 *     version: 1,
 *     defaults: () => ({ ... }),
 *     serialize: () => myLiveState,
 *     hydrate: (raw, ctx) => ctx.isRecord(raw) ? {...} : ctx.defaults,
 *   })
 *
 * Then call `save.request('venue:served')` after anything worth keeping.
 */

import './dayEvents'
import { bus } from './events'
import type { SaveSlot } from './types'

/**
 * 1 -> 2: "A DAY IN TOWN". Adds time, needs, errands, inventory, collection,
 * gifts and visit sections. See `ENVELOPE_MIGRATIONS[1]` — every pre-expansion
 * section is passed through BY REFERENCE and nothing existing is rewritten.
 */
export const SCHEMA_VERSION = 2
/**
 * The storage prefix is v1 ON PURPOSE and must never change. It is the KEY a
 * kid's town is stored under, not the schema version; bumping it would orphan
 * every existing save on every existing iPad. Schema versioning lives inside
 * the envelope (`v`), which is what `ENVELOPE_MIGRATIONS` upgrades.
 */
const KEY_PREFIX = 'mt.v1'
const AUTOSAVE_DEBOUNCE_MS = 900
const MAX_DEFER_MS = 8000

// ---------------------------------------------------------------------------
// envelope
// ---------------------------------------------------------------------------

export interface SectionBlob {
  v: number
  data: unknown
}

export interface SaveEnvelope {
  v: number
  slot: SaveSlot
  savedAt: number
  playtimeMs: number
  /** FNV-1a of JSON.stringify(sections). Detects truncation and tampering. */
  sum: string
  sections: Record<string, SectionBlob>
}

export interface HydrateContext<T> {
  readonly defaults: T
  readonly savedVersion: number
  readonly warn: (msg: string) => void
  readonly isRecord: (v: unknown) => v is Record<string, unknown>
  readonly num: (v: unknown, fallback: number) => number
  readonly str: (v: unknown, fallback: string) => string
  readonly bool: (v: unknown, fallback: boolean) => boolean
  readonly arr: <E>(v: unknown, map: (e: unknown, i: number) => E | null) => E[]
}

export interface SaveSection<T = unknown> {
  readonly id: string
  readonly version: number
  readonly defaults: () => T
  readonly serialize: () => T
  /** MUST NOT THROW. If it does, defaults are used and a warning is recorded. */
  readonly hydrate: (raw: unknown, ctx: HydrateContext<T>) => T
  /** Optional per-section migrations keyed by the version being upgraded FROM. */
  readonly migrate?: (raw: unknown, fromVersion: number) => unknown
}

export interface LoadReport {
  slot: SaveSlot
  existed: boolean
  recoveredFromBackup: boolean
  envelopeVersion: number
  warnings: string[]
  sections: string[]
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function fnv1a(s: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(16).padStart(8, '0')
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function makeHydrateCtx<T>(defaults: T, savedVersion: number, warnings: string[]): HydrateContext<T> {
  return {
    defaults,
    savedVersion,
    warn: (m) => warnings.push(m),
    isRecord,
    num: (v, f) => (typeof v === 'number' && Number.isFinite(v) ? v : f),
    str: (v, f) => (typeof v === 'string' ? v : f),
    bool: (v, f) => (typeof v === 'boolean' ? v : f),
    arr: <E>(v: unknown, map: (e: unknown, i: number) => E | null): E[] => {
      if (!Array.isArray(v)) return []
      const out: E[] = []
      for (let i = 0; i < v.length; i++) {
        try {
          const m = map(v[i], i)
          if (m !== null) out.push(m)
        } catch {
          /* a single bad row must never lose the rest of the town */
        }
      }
      return out
    },
  }
}

/** localStorage that can never throw into game code. */
const store = {
  get(key: string): string | null {
    try {
      return globalThis.localStorage?.getItem(key) ?? null
    } catch {
      return null
    }
  },
  set(key: string, value: string): boolean {
    try {
      globalThis.localStorage?.setItem(key, value)
      return true
    } catch {
      return false
    }
  },
  remove(key: string): void {
    try {
      globalThis.localStorage?.removeItem(key)
    } catch {
      /* ignore */
    }
  },
}

const mainKey = (slot: SaveSlot): string => `${KEY_PREFIX}.slot.${slot}`
const bakKey = (slot: SaveSlot): string => `${KEY_PREFIX}.bak.${slot}`
const pendKey = (slot: SaveSlot): string => `${KEY_PREFIX}.pend.${slot}`

/**
 * WHICH KID WAS PLAYING. Additive (ship QA).
 *
 * Held in its own tiny key, deliberately NOT inside either slot's envelope: it
 * is not part of a town, it must survive a corrupt slot, and writing it must
 * never wait on a save commit.
 *
 * Without it the app always booted slot A. Nothing was lost from disk — slot B's
 * town sat there intact — but the second kid closed the app on his own town and
 * reopened it on his brother's, with no reading and no memory of which picture
 * on the "which town" screen had been his. For a 3-year-old that is
 * indistinguishable from losing it.
 */
const ACTIVE_KEY = `${KEY_PREFIX}.active`

function readActiveSlot(): SaveSlot {
  return store.get(ACTIVE_KEY) === 'b' ? 'b' : 'a'
}

// ---------------------------------------------------------------------------
// envelope-level migrations (stub — v1 is the first shipped schema)
// ---------------------------------------------------------------------------

type EnvelopeMigration = (env: SaveEnvelope) => SaveEnvelope

/**
 * Keyed by the version being upgraded FROM. Add an entry when SCHEMA_VERSION
 * increments; never delete one, a kid's iPad may have been off for a year.
 */
/** A section blob only if it is not already there. Never overwrites a kid's data. */
function seed(
  sections: Record<string, SectionBlob>,
  id: string,
  version: number,
  data: unknown,
): void {
  if (sections[id] === undefined) sections[id] = { v: version, data }
}

export const ENVELOPE_MIGRATIONS: Record<number, EnvelopeMigration> = {
  /**
   * 1 -> 2 — "A DAY IN TOWN".
   *
   * THE ONE RULE OF THIS FUNCTION: it may ADD sections and it may not touch a
   * single byte of an existing one. Every pre-expansion blob — `placements`
   * (the whole town), `wallet` (coins and everything ever bought), `monster`,
   * `interiors`, `venue`, `npcs`, `town`, `audio`, `ui`, `econ`, `tutorial` —
   * is carried across by reference, unread and unrewritten. If this function
   * ever needs to reshape one of those, it must do it by ADDING a new section
   * and leaving the old one in place, because a kid's iPad may hold the only
   * copy of that town.
   *
   * The one genuinely derived value: the CLOCK'S DAY COUNTER is seeded from the
   * `town` section's existing `day` field, which pre-expansion saves have always
   * carried. Without this a kid who has been playing for forty days wakes up on
   * day one, and "the town remembers me" is the whole retention promise.
   *
   * Everything else is seeded EMPTY on purpose. A migration that invented an
   * inventory or a half-finished errand would be putting words in a kid's mouth.
   */
  1: (env) => {
    const sections: Record<string, SectionBlob> = { ...env.sections }

    // Carry the day counter across. Defensive: `town` may be missing or junk.
    let day = 1
    const townBlob = sections['town']
    if (townBlob !== undefined && isRecord(townBlob.data)) {
      const d = townBlob.data['day']
      if (typeof d === 'number' && Number.isFinite(d)) day = Math.max(1, Math.round(d))
    }

    // 7am on the day they left off. A returning town opens in the morning,
    // which is the one time of day where everything in the expansion is on.
    seed(sections, 'time', 1, { day, t: 7 / 24 })
    seed(sections, 'needs', 1, { acceptedOn: {} })
    seed(sections, 'errands', 1, { runs: [], done: [] })
    seed(sections, 'inventory', 1, { items: {} })
    seed(sections, 'collection', 1, { found: {} })
    seed(sections, 'gifts', 1, { outbox: [], inbox: [], claimedIds: [] })
    seed(sections, 'visit', 1, { lastVisitedAt: 0, photos: [] })

    return { ...env, v: 2, sections }
  },
}

function migrateEnvelope(env: SaveEnvelope, warnings: string[]): SaveEnvelope {
  let cur = env
  let guard = 0
  while (cur.v < SCHEMA_VERSION && guard++ < 64) {
    const m = ENVELOPE_MIGRATIONS[cur.v]
    if (m === undefined) {
      warnings.push(`no migration from envelope v${cur.v}; loading best-effort`)
      return { ...cur, v: SCHEMA_VERSION }
    }
    cur = m(cur)
  }
  if (cur.v > SCHEMA_VERSION) {
    warnings.push(`save is from a NEWER build (v${cur.v}); loading best-effort`)
  }
  return cur
}

// ---------------------------------------------------------------------------
// SaveManager
// ---------------------------------------------------------------------------

export class SaveManager {
  private sections = new Map<string, SaveSection<unknown>>()
  /** Resumes on whichever kid was last playing; 'a' for a brand new browser. */
  private slot: SaveSlot = readActiveSlot()
  private dirty = false
  private writing = false
  private timer: ReturnType<typeof setTimeout> | null = null
  private deferDepth = 0
  private deferSince = 0
  private pendingReason = ''
  private startedAt = Date.now()
  private playtimeMs = 0
  /** Set true by the harness so tests never race an async autosave. */
  autosaveEnabled = true

  /**
   * VISIT MODE — "look, don't touch", guaranteed at the save layer.
   *
   * When a kid walks into their brother's town, `visitingSlot` is the slot being
   * LOOKED AT and `hostSlot` is the kid who is actually playing. While it is
   * non-null:
   *
   *   - `commit()` refuses outright, so NO bytes are written to ANY slot. The
   *     visitor's own town is already on disk, untouched, and the host's live
   *     game state has been replaced by the other kid's — writing either would
   *     be a catastrophe, so nothing writes.
   *   - `setSlot()` and `useSlot()` refuse, so the active-slot pointer cannot
   *     drift onto the visited town and start autosaving into it later.
   *   - Nothing anywhere in this class writes to a slot other than `this.slot`.
   *     That is the structural guarantee: there is no `write(slot, ...)` taking
   *     a slot argument at all, so "write to the other town" is not a code path
   *     that exists to be reached by accident.
   *
   * A visit ALWAYS ends by reloading the host's own slot from disk, so nothing
   * the visited town put into memory can survive into the visitor's save.
   */
  private visitingSlot: SaveSlot | null = null
  private hostSlot: SaveSlot | null = null

  constructor() {
    bus.on('scene:transitionStart', () => this.beginTransition())
    bus.on('scene:transitionEnd', () => this.endTransition())
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush('visibilityHidden')
      })
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flush('pagehide'))
    }
  }

  // -- sections ----------------------------------------------------------

  register<T>(section: SaveSection<T>): void {
    if (this.sections.has(section.id)) {
      throw new Error(`save section "${section.id}" registered twice`)
    }
    this.sections.set(section.id, section as SaveSection<unknown>)
  }

  sectionIds(): string[] {
    return [...this.sections.keys()]
  }

  // -- slots -------------------------------------------------------------

  get activeSlot(): SaveSlot {
    return this.slot
  }

  /**
   * Point at a different slot WITHOUT loading it. Flushes the current slot
   * first so the kid you are switching away from never loses anything.
   * You almost always want `useSlot()` instead.
   */
  setSlot(slot: SaveSlot): void {
    // A visitor cannot pick up a different town while standing in one.
    if (this.visitingSlot !== null) {
      console.warn('[save] setSlot refused: a visit is open. Call endVisit() first.')
      return
    }
    if (slot === this.slot) return
    this.flush('slotSwitch')
    this.slot = slot
    // Remember WHO is playing before anything else can fail. This write is one
    // character and cannot be blocked by a transition guard, so the app always
    // reopens on the town the kid was last in.
    store.set(ACTIVE_KEY, slot)
    bus.emit('save:slotChanged', { slot })
  }

  /**
   * THE slot-picker call: commit the current kid's town, switch, and hydrate
   * the other kid's town into every section. The two slots are completely
   * independent; nothing leaks between them.
   */
  useSlot(slot: SaveSlot): LoadReport {
    if (this.visitingSlot !== null) {
      console.warn('[save] useSlot refused: a visit is open. Call endVisit() first.')
      return {
        slot: this.slot,
        existed: this.hasSave(this.slot),
        recoveredFromBackup: false,
        envelopeVersion: SCHEMA_VERSION,
        warnings: ['useSlot refused during a visit'],
        sections: [],
      }
    }
    this.setSlot(slot)
    return this.load(slot)
  }

  // -- visiting (READ-ONLY BY CONSTRUCTION) ------------------------------

  get visiting(): boolean {
    return this.visitingSlot !== null
  }
  /** The town being looked at, or null. */
  get visitSlot(): SaveSlot | null {
    return this.visitingSlot
  }
  /** The kid who is actually playing. Their slot is where any write would go. */
  get visitHostSlot(): SaveSlot | null {
    return this.hostSlot
  }

  /**
   * Walk into the other kid's town. Commits the host's own town first (that is
   * the last write until the visit ends), then hydrates every section from the
   * OTHER slot's bytes without ever pointing `this.slot` at it.
   *
   * Returns false if there is nothing there to visit — an empty slot is not an
   * error, it just means the brother has not made a town yet.
   */
  beginVisit(slot: SaveSlot): boolean {
    if (this.visitingSlot !== null) return this.visitingSlot === slot
    if (slot === this.slot) return false
    if (!this.hasSave(slot)) return false
    this.flush('beforeVisit')
    this.hostSlot = this.slot
    this.visitingSlot = slot
    // Hydrate from the visited bytes. `hydrateFrom` never touches `this.slot`
    // and never writes anything.
    this.hydrateFrom(slot)
    bus.emit('save:visitBegan', { slot, host: this.hostSlot })
    return true
  }

  /**
   * Go home. ALWAYS reloads the host's own town from disk, so nothing seen or
   * poked in the other kid's world can leak into this kid's save.
   */
  endVisit(): LoadReport | null {
    const visited = this.visitingSlot
    const host = this.hostSlot ?? this.slot
    if (visited === null) return null
    this.visitingSlot = null
    this.hostSlot = null
    // Whatever the visited town wrote into live memory is discarded wholesale.
    this.dirty = false
    const report = this.load(host)
    bus.emit('save:visitEnded', { slot: visited, host })
    return report
  }

  /**
   * READ-ONLY peek at another slot's section blobs, migrated to the current
   * schema. This is how a gift crosses between towns WITHOUT a write path: the
   * receiving kid reads the giver's outbox and files it into their OWN save.
   *
   * Returns a structuredClone, so a caller cannot hold a reference into
   * anything that might later be written.
   */
  peekSections(slot: SaveSlot): Record<string, SectionBlob> | null {
    const env = this.readEnvelope(mainKey(slot)) ?? this.readEnvelope(bakKey(slot))
    if (env === null) return null
    const migrated = migrateEnvelope(env, [])
    try {
      return JSON.parse(JSON.stringify(migrated.sections)) as Record<string, SectionBlob>
    } catch {
      return null
    }
  }

  /** Hydrate every section from a slot's bytes WITHOUT making it active. */
  private hydrateFrom(slot: SaveSlot): void {
    let env = this.readEnvelope(mainKey(slot)) ?? this.readEnvelope(bakKey(slot))
    if (env !== null) env = migrateEnvelope(env, [])
    for (const [id, s] of this.sections) {
      const defaults = s.defaults()
      const blob = env?.sections[id]
      if (blob === undefined) {
        this.applySection(id, defaults)
        continue
      }
      let raw: unknown = blob.data
      const fromV = typeof blob.v === 'number' ? blob.v : 0
      try {
        if (fromV !== s.version && s.migrate !== undefined) raw = s.migrate(raw, fromV)
      } catch {
        raw = undefined
      }
      let value: unknown
      try {
        value = s.hydrate(raw, makeHydrateCtx(defaults, fromV, []))
        if (value === undefined || value === null) value = defaults
      } catch {
        value = defaults
      }
      this.applySection(id, value)
    }
  }

  hasSave(slot: SaveSlot): boolean {
    return store.get(mainKey(slot)) !== null || store.get(bakKey(slot)) !== null
  }

  /** Summary for the slot-picker screen. Never throws. */
  peek(slot: SaveSlot): { exists: boolean; savedAt: number; playtimeMs: number } {
    const env = this.readEnvelope(mainKey(slot)) ?? this.readEnvelope(bakKey(slot))
    if (env === null) return { exists: false, savedAt: 0, playtimeMs: 0 }
    return { exists: true, savedAt: env.savedAt, playtimeMs: env.playtimeMs }
  }

  // -- transitions -------------------------------------------------------

  beginTransition(): void {
    if (this.deferDepth === 0) this.deferSince = Date.now()
    this.deferDepth++
  }

  endTransition(): void {
    this.deferDepth = Math.max(0, this.deferDepth - 1)
    if (this.deferDepth === 0 && this.dirty) this.flush('transitionEnd')
  }

  private get blocked(): boolean {
    // Safety valve: a crew that forgets to close a transition can't block saves
    // forever. After MAX_DEFER_MS we write anyway.
    if (this.deferDepth === 0) return false
    if (Date.now() - this.deferSince > MAX_DEFER_MS) {
      console.warn('[save] transition guard held too long — forcing a commit')
      this.deferDepth = 0
      return false
    }
    return true
  }

  // -- writing -----------------------------------------------------------

  /** Autosave trigger. Call after ANY meaningful change. Cheap, debounced. */
  request(reason: string): void {
    this.dirty = true
    this.pendingReason = reason
    bus.emit('save:requested', { reason })
    if (!this.autosaveEnabled) return
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.timer = null
      this.flush(this.pendingReason)
    }, AUTOSAVE_DEBOUNCE_MS)
  }

  /** Write now (unless blocked by a transition). Returns true if bytes landed. */
  flush(reason: string): boolean {
    if (!this.dirty) return false
    if (this.writing) return false
    if (this.blocked) return false
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }
    return this.commit(reason)
  }

  private commit(reason: string): boolean {
    /**
     * THE HARD STOP FOR VISIT MODE. While a kid is standing in their brother's
     * town, live game state IS their brother's town — so a write here would put
     * one kid's world into the other kid's file. There is no "write it to the
     * right slot instead" branch on purpose: the only correct action is not to
     * write at all, and `endVisit()` reloads the host from disk afterwards.
     */
    if (this.visitingSlot !== null) {
      if (this.debugLog) console.log(`[save] commit refused during a visit (${reason})`)
      return false
    }
    const slot = this.slot
    const t0 = Date.now()
    this.writing = true
    try {
      const sections: Record<string, SectionBlob> = {}
      for (const [id, s] of this.sections) {
        try {
          sections[id] = { v: s.version, data: s.serialize() }
        } catch (err) {
          // Keep whatever was previously saved for this section rather than
          // dropping it: a broken serializer must not delete a kid's rooms.
          const prev = this.readEnvelope(mainKey(slot))?.sections[id]
          console.error(`[save] section "${id}" failed to serialize`, err)
          if (prev !== undefined) sections[id] = prev
        }
      }

      const body = JSON.stringify(sections)
      const env: SaveEnvelope = {
        v: SCHEMA_VERSION,
        slot,
        savedAt: Date.now(),
        playtimeMs: this.playtimeMs + (Date.now() - this.startedAt),
        sum: fnv1a(body),
        sections,
      }
      const text = JSON.stringify(env)

      // 1. stage
      if (!store.set(pendKey(slot), text)) {
        bus.emit('save:failed', { slot, error: 'storage write refused (quota?)' })
        return false
      }
      // 2. verify the staged copy actually reads back intact
      const verify = this.parse(store.get(pendKey(slot)))
      if (verify === null || verify.sum !== env.sum) {
        store.remove(pendKey(slot))
        bus.emit('save:failed', { slot, error: 'staged save failed verification' })
        return false
      }
      // 3. roll the previous good save into the backup slot
      const prevMain = store.get(mainKey(slot))
      if (prevMain !== null && this.parse(prevMain) !== null) {
        store.set(bakKey(slot), prevMain)
      }
      // 4. promote
      store.set(mainKey(slot), text)
      store.remove(pendKey(slot))

      this.dirty = false
      const ms = Date.now() - t0
      bus.emit('save:committed', { slot, bytes: text.length, ms })
      if (this.debugLog) console.log(`[save] committed slot ${slot} (${reason}) ${text.length}b ${ms}ms`)
      return true
    } catch (err) {
      bus.emit('save:failed', { slot, error: String(err) })
      return false
    } finally {
      this.writing = false
    }
  }

  debugLog = false

  // -- reading -----------------------------------------------------------

  private parse(text: string | null): SaveEnvelope | null {
    if (text === null || text.length === 0) return null
    try {
      const raw: unknown = JSON.parse(text)
      if (!isRecord(raw)) return null
      if (typeof raw['v'] !== 'number') return null
      if (!isRecord(raw['sections'])) return null
      const sections = raw['sections'] as Record<string, SectionBlob>
      const sum = typeof raw['sum'] === 'string' ? raw['sum'] : ''
      if (sum.length > 0 && fnv1a(JSON.stringify(sections)) !== sum) return null
      return {
        v: raw['v'],
        slot: (raw['slot'] === 'b' ? 'b' : 'a') as SaveSlot,
        savedAt: typeof raw['savedAt'] === 'number' ? raw['savedAt'] : 0,
        playtimeMs: typeof raw['playtimeMs'] === 'number' ? raw['playtimeMs'] : 0,
        sum,
        sections,
      }
    } catch {
      return null
    }
  }

  private readEnvelope(key: string): SaveEnvelope | null {
    return this.parse(store.get(key))
  }

  /**
   * Load a slot into every registered section. Always succeeds: worst case
   * every section gets its defaults and the kid starts a fresh town.
   */
  load(slot: SaveSlot = this.slot): LoadReport {
    this.slot = slot
    const warnings: string[] = []
    let recovered = false

    let env = this.readEnvelope(mainKey(slot))
    if (env === null) {
      const bak = this.readEnvelope(bakKey(slot))
      if (bak !== null) {
        env = bak
        recovered = true
        warnings.push('main save was missing or corrupt — recovered from backup')
      }
    }

    const existed = env !== null
    if (env !== null) env = migrateEnvelope(env, warnings)

    const loaded: string[] = []
    for (const [id, s] of this.sections) {
      const defaults = s.defaults()
      const blob = env?.sections[id]
      if (blob === undefined) {
        this.applySection(id, defaults)
        loaded.push(id)
        continue
      }
      let raw: unknown = blob.data
      const fromV = typeof blob.v === 'number' ? blob.v : 0
      try {
        if (fromV !== s.version && s.migrate !== undefined) raw = s.migrate(raw, fromV)
      } catch (err) {
        warnings.push(`section "${id}" migration failed: ${String(err)}`)
        raw = undefined
      }
      let value: unknown
      try {
        value = s.hydrate(raw, makeHydrateCtx(defaults, fromV, warnings))
        if (value === undefined || value === null) value = defaults
      } catch (err) {
        warnings.push(`section "${id}" hydrate threw: ${String(err)} — using defaults`)
        value = defaults
      }
      this.applySection(id, value)
      loaded.push(id)
    }

    this.playtimeMs = env?.playtimeMs ?? 0
    this.startedAt = Date.now()
    this.dirty = false

    bus.emit('save:loaded', {
      slot,
      version: env?.v ?? SCHEMA_VERSION,
      recoveredFromBackup: recovered,
    })
    if (warnings.length > 0) console.warn('[save] load warnings:', warnings)

    return {
      slot,
      existed,
      recoveredFromBackup: recovered,
      envelopeVersion: env?.v ?? SCHEMA_VERSION,
      warnings,
      sections: loaded,
    }
  }

  /** Sections receive their hydrated value through this callback registry. */
  private applyHandlers = new Map<string, (value: unknown) => void>()

  onApply<T>(sectionId: string, fn: (value: T) => void): void {
    this.applyHandlers.set(sectionId, fn as (value: unknown) => void)
  }

  private applySection(id: string, value: unknown): void {
    const fn = this.applyHandlers.get(id)
    if (fn === undefined) return
    try {
      fn(value)
    } catch (err) {
      console.error(`[save] apply handler for "${id}" threw`, err)
    }
  }

  // -- destructive ops (deliberately explicit) ---------------------------

  /** Wipe a slot. Backup is kept so a mis-tap is still recoverable in dev. */
  eraseSlot(slot: SaveSlot): void {
    const main = store.get(mainKey(slot))
    if (main !== null) store.set(bakKey(slot), main)
    store.remove(mainKey(slot))
    store.remove(pendKey(slot))
  }

  /**
   * Wipe a slot AND its backup. Additive (ship QA), and deliberately separate
   * from `eraseSlot`.
   *
   * `eraseSlot` keeps the backup on purpose, which means it cannot replay a
   * genuine first launch: `load()` falls straight back to the backup, finds a
   * monster already created, and the creator never opens. A QA run that erased
   * and reloaded was therefore reviewing turn TWO while reporting turn one.
   *
   * There is no path to this from anything a kid can tap — the slot picker has
   * no erase button at all, by design.
   */
  eraseSlotHard(slot: SaveSlot): void {
    store.remove(mainKey(slot))
    store.remove(bakKey(slot))
    store.remove(pendKey(slot))
  }

  /** Export a slot as text — the "show a grown-up" / rescue hatch. */
  exportSlot(slot: SaveSlot): string | null {
    return store.get(mainKey(slot))
  }

  importSlot(slot: SaveSlot, text: string): boolean {
    if (this.parse(text) === null) return false
    const prev = store.get(mainKey(slot))
    if (prev !== null) store.set(bakKey(slot), prev)
    return store.set(mainKey(slot), text)
  }
}

export const save = new SaveManager()

/** Sugar so crews do not have to reach for the singleton twice. */
export function registerSaveSection<T>(section: SaveSection<T>, apply: (value: T) => void): void {
  save.register(section)
  save.onApply<T>(section.id, apply)
}

// ---------------------------------------------------------------------------
// CORRUPTION SELF-TEST  (harness: window.__harness.saveSelfTest())
// ---------------------------------------------------------------------------

export interface SaveTestReport {
  name: string
  pass: boolean
  detail: string
}

/**
 * Deliberately corrupts a scratch slot and proves the rules hold. Run in CI via
 * `npm run drive` and from the harness. Uses its OWN storage keys so a kid's
 * towns are never touched.
 */
export function runSaveSelfTest(): { ok: boolean; results: SaveTestReport[] } {
  const results: SaveTestReport[] = []
  const testSlot = 'a' as SaveSlot
  const K = { main: `${KEY_PREFIX}.test.slot.${testSlot}`, bak: `${KEY_PREFIX}.test.bak.${testSlot}` }
  const push = (name: string, pass: boolean, detail: string): void => {
    results.push({ name, pass, detail })
  }

  const good = JSON.stringify({
    v: SCHEMA_VERSION,
    slot: testSlot,
    savedAt: 1,
    playtimeMs: 0,
    sum: fnv1a(JSON.stringify({ town: { v: 1, data: { name: 'GoodTown' } } })),
    sections: { town: { v: 1, data: { name: 'GoodTown' } } },
  })

  // 1. checksum rejects tampering
  const tampered = good.replace('GoodTown', 'BadTownX')
  const mgr = new SaveManager()
  const parse = (t: string): SaveEnvelope | null =>
    (mgr as unknown as { parse: (s: string | null) => SaveEnvelope | null }).parse(t)
  push('checksum accepts an intact envelope', parse(good) !== null, 'parse(good) != null')
  push('checksum rejects a tampered envelope', parse(tampered) === null, 'parse(tampered) == null')

  // 2. truncation is rejected
  push('truncated JSON rejected', parse(good.slice(0, good.length - 20)) === null, 'half a file')

  // 3. total garbage is rejected
  push('garbage rejected', parse('{{{not json') === null, 'unparseable')
  push('empty rejected', parse('') === null, 'empty string')

  // 4. backup recovery: corrupt main + good backup -> backup wins
  store.set(K.main, '@@corrupt@@')
  store.set(K.bak, good)
  const mainBad = parse(store.get(K.main) ?? '') === null
  const bakGood = parse(store.get(K.bak) ?? '') !== null
  push('corrupt main detected', mainBad, 'main fails parse')
  push('backup survives and parses', bakGood, 'backup usable for recovery')

  // 5. a hydrate that throws yields defaults instead of exploding
  const warnings: string[] = []
  const ctx = makeHydrateCtx({ ok: true }, 1, warnings)
  let defensive = false
  try {
    const hydrate = (): { ok: boolean } => {
      throw new Error('boom')
    }
    try {
      hydrate()
    } catch {
      defensive = ctx.defaults.ok === true
    }
  } catch {
    defensive = false
  }
  push('hydrate failure falls back to defaults', defensive, 'defaults reachable')

  // 6. helper coercions never throw on junk
  const coerced =
    ctx.num('nope', 7) === 7 &&
    ctx.str(42, 'x') === 'x' &&
    ctx.bool(null, true) === true &&
    ctx.arr<number>({ not: 'an array' }, () => 1).length === 0
  push('coercion helpers reject junk', coerced, 'num/str/bool/arr')

  store.remove(K.main)
  store.remove(K.bak)

  return { ok: results.every((r) => r.pass), results }
}
